<script setup lang="ts">
import AmberAlertLogo from 'assets/logos/amber-alert-logo.svg?url'
import Facebook from 'assets/logos/facebook-logo.svg?url'
import Instagram from 'assets/icons/instagram.svg?url'
import TikTok from 'assets/icons/tiktok.svg?url'

const { t } = useI18n()

const route = useRoute()

const linkList = computed(() => {
    return [
        {
            altTxt: t('confirmation.infos.altTxt0'),
            label: t('confirmation.infos.label0'),
            url: 'https://www.amber-alert-deutschland.de/',
            icon: AmberAlertLogo,
            tracking: [
                { track: 'click' },
                { 'track-position': 1 },
                { 'track-name': t('confirmation.infos.label0') },
                { 'track-url': route.fullPath }
            ]
        },
        {
            altTxt: t('confirmation.infos.altTxt1'),
            label: t('confirmation.infos.label1'),
            url: 'https://www.facebook.com/deutschlandfindeteuch/',
            icon: Facebook,
            tracking: [
                { track: 'click' },
                { 'track-position': 1 },
                { 'track-name': t('confirmation.infos.label1') },
                { 'track-url': route.fullPath }
            ]
        },
        {
            altTxt: t('confirmation.infos.altTxt2'),
            label: t('confirmation.infos.label2'),
            url: 'https://www.instagram.com/vermisste_kinder_de/',
            icon: Instagram,
            tracking: [
                { track: 'click' },
                { 'track-position': 1 },
                { 'track-name': t('confirmation.infos.label2') },
                { 'track-url': route.fullPath }
            ]
        },
        {
            altTxt: t('confirmation.infos.altTxt3'),
            label: t('confirmation.infos.label3'),
            url: 'https://www.tiktok.com/@vermis_st',
            icon: TikTok,
            tracking: [
                { track: 'click' },
                { 'track-position': 1 },
                { 'track-name': t('confirmation.infos.label3') },
                { 'track-url': route.fullPath }
            ]
        }
    ]
})

const sectionTrackingAttributes = computed(() => {
    return [
        { track: 'view' },
        { 'track-position': 3 },
        { section: 'confirmation-infos', },
        { 'track-name': t('confirmation.infos.headline') },
        { 'track-url': route.fullPath }
    ]
})

const sectionTracking = computed(() => {
    if (!sectionTrackingAttributes.value) return {}

    const attributes = {}

    sectionTrackingAttributes.value.forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
})

const dataAttributes = (tracking) => {
    if (!tracking) return {}

    const attributes = {}
    tracking.forEach((obj) => {
        for (const [key, value] of Object.entries(obj)) {
            attributes[`data-${key}`] = value
        }
    })
    return attributes
}
</script>

<template>
    <section class="confirmation-infos-wrapper" v-bind="sectionTracking">
        <div class="confirmation-infos-container">
            <Subline :subline-config="{ title: t('confirmation.infos.subline') }" />
            <ContentHeadline :content-headline-config="{ title: t('confirmation.infos.headline') }" />

            <ul class="confirmation-infos-link-list">
                <li v-for="({ label, icon, url, altTxt, tracking }, index) in linkList" :key="index + label" class="confirmation-infos-link-list-item">
                    <NuxtLink :to="url" rel="noreferrer nofollow" target="_blank" v-bind="dataAttributes(tracking)">
                        <img :src="icon" :alt="altTxt" width="50" height="50">
                        <span v-html="label" />
                    </NuxtLink>
                </li>
            </ul>

            <p v-html=" t('confirmation.infos.copy')" />
        </div>
    </section>
</template>

<style lang="scss">
.confirmation-infos-wrapper {
    overflow: hidden;
    position: relative;
    grid-column: 1 / -1;
    text-align: center;
    @include fullbleed;

    &::before {
        content: '';
        position: absolute;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 50%;
        pointer-events: none;
        background: $home-infos-gradient;
        z-index: 0;
    }
}

.confirmation-infos-container {
    grid-column: 2 / -2;
    position: relative;
    z-index: 1;
    @include section-padding;

    p {
        margin: 0;
    }
}

.confirmation-infos-link-list {
    display: grid;
    margin-block: px2rem(100);
    list-style-type: none;
    padding: 0;
    margin: 0 0 px2rem(80);
    grid-template-columns: repeat(2, 1fr);

    @include bp(medium1) {
        grid-template-columns: repeat(4, 1fr);
    }

    @include bp(large1) {
        margin: 0 0 px2rem(100);
    }
}

.confirmation-infos-link-list-item {
    font-size: px2rem(14);
    padding: px2rem(40) 0;
    border-right: $confirmation-infos-link-list-item-border 1px solid;

    &:last-child {
        border-right: none;
    }

    @include bp(small2) {
        &:nth-child(2) {
            border-right: none;
        }
    }

    @include bp(medium1) {
        &:nth-child(2) {
            border-right: $confirmation-infos-link-list-item-border 1px solid;
        }
    }

    @media (hover: hover) {
       &:hover {
           img {
               transform: scale(1.1);
           }
       }
    }

    a {
        display: flex;
        justify-content: center;
        flex-flow: row wrap;
        height: 100%;
        color: inherit;
        text-decoration: none;
    }

    img {
        fill: $body-text;
        max-height: 60px;
        max-width: 80px;
        display: block;
        margin: 0 auto px2rem(20);
        transition: transform 0.4s ease-in-out;
        width: 100%;
        height: auto;

        @include bp(large1) {
            max-height: 100px;
            max-width: 150px;
            margin: 0 auto px2rem(40);
        }
    }

    span {
        display: block;
        margin-top: auto;
        width: 100%;
        padding: 0 px2rem(20);
    }
}
</style>
